import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Email, Lock } from "@mui/icons-material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useMutationByMethod } from "../services/hooks/useMutationByMethod";
import { GetUrl, PostUrl } from "../api/endpointConfig";
import { ToastService } from "../utils/toastService";
import { useLoader } from "../Loader/LoaderContext";
import { useNavigate } from "react-router-dom";
import { useGet } from "../services/hooks/useGet";

interface LoginFormProps {
  onForgot: (statevalue: boolean) => void;
}
const ForgotPassword: React.FC<LoginFormProps> = ({ onForgot }) => {
  const [step, setStep] = useState<"email" | "otp" | "reset">("email");
  const [email, setEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [openOtpDialog, setOpenOtpDialog] = useState(false);
  const [otpTimer, setOtpTimer] = useState(90);
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();

  const { mutate: sendOtp } = useMutationByMethod<any>(
    "post",
    `${PostUrl.emailOtp}?email=${encodeURIComponent(email)}`
  );
  const { mutate: verifyOtp } = useMutationByMethod<any>(
    "post",
    `${PostUrl.auth}/verify-otp`
  );
  const { mutate: resetPassword } = useMutationByMethod<any>(
    "post",
    `${PostUrl.forgetPassword}`
  );

  const { data, refetch } = useGet<any>(
    email && otpValue
      ? `${GetUrl.getEmailVerify}?email=${email}&otp=${otpValue}`
      : "",
    {},
    false
  );

  // Step 1 → Request OTP
  const handleEmailSubmit = (values: { email: string }) => {
    setEmail(values.email);
    showLoader();
    sendOtp(null, {
      onSuccess: () => {
        ToastService.success("OTP sent successfully!");
        setEmail(values.email);
        setOpenOtpDialog(true);
        // setStep("otp");
        hideLoader();
      },
      onError: (err: any) => {
        ToastService.error(err.data.detail || "Failed to send OTP");
        hideLoader();
      },
    });
  };

  // Step 2 → Verify OTP
  const handleOtpVerify = (values: { otp: string }) => {
    // showLoader();
    setOtpValue(values.otp);
     
  };

  useEffect(() => {
      // if (!email && !otpValue) return;

      const verifyEmail = async () => {
        showLoader();
        try {
          const { data } = await refetch();
        console.log("data", data);

        ToastService.success("OTP verified!");
        setOpenOtpDialog(false);
        setStep("reset");
        hideLoader();
        setOtpToken(data);
        } catch (error: any) {
          ToastService.error(error?.message || "Verification failed");
        } finally {
          hideLoader();
        }
      };
      if (otpValue.length === 6){
        verifyEmail();
      }
      
    }, [email, otpValue]);

  // Step 3 → Reset Password
  const handlePasswordReset = (values: {
    password: string;
    confirmPassword: string;
  }) => {
    showLoader();
    const obj = {
      token: otpToken,
      new_password: values.password,
    };
    resetPassword(obj, {
      onSuccess: () => {
        ToastService.success("Password reset successfully!");
        onForgot(false);
        hideLoader();
      },
      onError: (err: any) => {
        ToastService.error(err.data.detail || "Reset failed");
        hideLoader();
      },
    });
  };

  // Validation Schemas
  const emailSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
  });
  const otpSchema = Yup.object().shape({
    otp: Yup.string().required("OTP is required"),
  });
  const resetSchema = Yup.object().shape({
    password: Yup.string().min(6, "At least 6 chars").required("Required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Required"),
  });
  

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (openOtpDialog && otpTimer > 0) {
      timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [openOtpDialog, otpTimer]);

  useEffect(() => {
    if (!openOtpDialog) {
      setOtpTimer(90);
    }
  }, [openOtpDialog]);

  return (
    <Box className="forgot-page" width={"100%"}>
      <Typography variant="h6" mb={2}>
        Forgot Password
      </Typography>

      {/* Step 1 → Email */}
      {step === "email" && (
        <Formik
          initialValues={{ email: "" }}
          validationSchema={emailSchema}
          onSubmit={handleEmailSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <Box className="form-group">
                <Field
                  as={TextField}
                  name="email"
                  label="Email Address"
                  fullWidth
                  variant="outlined"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box
                className="buttonSec"
                display="flex"
                justifyContent={"center"}
                gap={2}
              >
                <Button
                  type="button"
                  variant="contained"
                  color="info"
                  className="submit-button"
                  onClick={() => {
                    onForgot(false);
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="submit-button"
                >
                  Verify
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      )}

      {/* Step 2 → OTP Dialog */}
      <Dialog open={openOtpDialog} onClose={() => setOpenOtpDialog(false)}  >
        <DialogTitle>OTP Verification</DialogTitle>
        <Box px={3} py={1} width={"24rem"}>
          <Typography variant="body2">
            Please check your inbox or spam folder for the one-time password (OTP). The OTP has been sent to the email address you provided. 
          </Typography>
        </Box>
        <Box px={3} py={1} width={"24rem"} textAlign={'center'}>
          <Typography variant="body2">
           <span className="boldText">{email} </span>
          </Typography>
        </Box>
        <Formik
          initialValues={{ otp: "" }}
          validationSchema={otpSchema}
          onSubmit={handleOtpVerify}
        >
          {({ errors, touched }) => (
            <Form>
              <DialogContent>
                <Box className="form-group"  ml={'auto'} mr={'auto'}>
                  <Field
                    as={TextField}
                    name="otp"
                    label="Enter OTP"
                    fullWidth
                    variant="outlined"
                    error={touched.otp && Boolean(errors.otp)}
                    helperText={touched.otp && errors.otp}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 6,
                    }}
                  />
                </Box>
                <Box mt={2} textAlign="center">
                  <Typography variant="body2">
                    Didn't receive the OTP?{" "}
                    <Button
                      variant="text"
                      color="primary"
                      disabled={otpTimer > 0}
                      size="small"
                      onClick={() => {
                        if (!email) {
                          ToastService.error("Please enter your email first.");
                          return;
                        }
                        showLoader();
                        sendOtp(null, {
                          onSuccess: () => {
                            ToastService.success("OTP resent successfully!");
                            hideLoader();
                            setOtpTimer(90);
                          },
                          onError: (err: any) => {
                            ToastService.error(
                              err.data.detail || "Failed to resend OTP"
                            );
                            hideLoader();
                          },
                        });
                      }}
                    >
                      Resend OTP
                    </Button>
                  </Typography>
                  {otpTimer > 0 &&
                  <Typography variant="body2" color="textSecondary">
                    {`Resend OTP in ${Math.max(0, otpTimer)}s`}
                  </Typography>
                   }
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenOtpDialog(false)}>Cancel</Button>
                <Button type="submit" variant="contained" color="primary">
                  Verify
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Step 3 → Reset Password */}
      {step === "reset" && (
        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          validationSchema={resetSchema}
          onSubmit={handlePasswordReset}
        >
          {({ errors, touched }) => (
            <Form>
              <Box className="form-group">
                <Box mb={2}>
                  <Field
                    as={TextField}
                    name="password"
                    label="New Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box mb={2}>
                  <Field
                    as={TextField}
                    name="confirmPassword"
                    label="Confirm New Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    error={
                      touched.confirmPassword && Boolean(errors.confirmPassword)
                    }
                    helperText={
                      touched.confirmPassword && errors.confirmPassword
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box
                  className="buttonSec"
                  display="flex"
                  justifyContent={"center"}
                  gap={2}
                >
                  <Button
                    type="button"
                    variant="contained"
                    color="info"
                    className="submit-button"
                    onClick={() => {
                      onForgot(false);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="info"
                    className="submit-button"
                  >
                    Reset Password
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default ForgotPassword;
